import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Trash2, Upload, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export default function AdminMedia() {
  const [media, setMedia] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [altEdits, setAltEdits] = useState({});
  const [copied, setCopied] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get("/admin/media");
      setMedia(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const data = new FormData();
      Array.from(fileList).forEach((f) => data.append("files", f));
      await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Upload complete");
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveAlt = async (m) => {
    try {
      await api.patch(`/admin/media/${m.id}`, { alt_text: altEdits[m.id] ?? m.alt_text ?? "" });
      toast.success("Alt text saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyUrl = (m) => {
    navigator.clipboard.writeText(`${window.location.origin}${m.url}`);
    setCopied(m.id);
    setTimeout(() => setCopied(""), 1500);
  };

  const remove = async (m, force = false) => {
    try {
      await api.delete(`/admin/media/${m.id}`, { params: { force } });
      toast.success("Media deleted");
      fetchAll();
    } catch (err) {
      if (err.response?.status === 409) {
        const usages = err.response.headers["x-usages"] || "website content";
        if (window.confirm(`This image is currently used by: ${usages}.\n\nDelete it anyway? Those places will show a broken image.`)) {
          remove(m, true);
        }
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-black uppercase text-zinc-900">Media Library</h1>
          <p className="text-sm text-zinc-500">Reusable images for hero sections, categories, pages, and branding.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm px-4 py-2.5 cursor-pointer text-sm" data-testid="media-upload-button">
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="media-upload-input" />
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Upload Images"}
        </label>
      </div>

      {!media ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>
      ) : media.length === 0 ? (
        <div className="border border-dashed border-zinc-200 p-14 text-center text-zinc-500" data-testid="media-empty">
          No media yet. Upload images to use them across the website.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} className="bg-white border border-zinc-200 overflow-hidden" data-testid={`media-item-${m.id}`}>
              <div className="aspect-video bg-zinc-100">
                <img src={resolveImageUrl(m.url)} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3 space-y-2">
                <p className="text-xs text-zinc-500 truncate font-mono">{m.original_filename}</p>
                <p className="text-[10px] text-zinc-500">{fmtDate(m.created_at)} · {Math.round((m.size || 0) / 1024)} KB</p>
                <div className="flex gap-1.5">
                  <input
                    value={altEdits[m.id] ?? m.alt_text ?? ""}
                    onChange={(e) => setAltEdits((a) => ({ ...a, [m.id]: e.target.value }))}
                    placeholder="Alt text"
                    className="h-8 flex-1 rounded-sm border border-zinc-300 bg-white px-2 text-xs text-zinc-800 focus:outline-none focus:ring-1 focus:ring-[#FF6B00]"
                    data-testid={`media-alt-${m.id}`}
                  />
                  <button onClick={() => saveAlt(m)} className="h-8 px-2 border border-zinc-300 text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00] rounded-sm text-[10px] font-bold uppercase" data-testid={`media-alt-save-${m.id}`}>
                    Save
                  </button>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => copyUrl(m)} className="flex-1 h-8 inline-flex items-center justify-center gap-1 border border-zinc-300 text-zinc-500 hover:text-[#FF6B00] hover:border-[#FF6B00] rounded-sm text-[10px] font-bold uppercase" data-testid={`media-copy-${m.id}`}>
                    {copied === m.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} Copy URL
                  </button>
                  <button onClick={() => remove(m)} className="h-8 px-2 border border-zinc-300 text-zinc-500 hover:text-red-600 hover:border-red-500 rounded-sm" aria-label="Delete media" data-testid={`media-delete-${m.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
